import React, { useState } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';
import { useMfaFeature } from '../../hooks/useFeatureFlags';
import { Card, CardHeader, CardContent } from '../ui/card';
import { FormGroup, FormLabel, FormInput } from '../ui/form';
import { Button } from '../ui/button';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle
} from 'lucide-react';

// Toggle component for MFA enable/disable
const Toggle = ({ enabled, onChange, disabled = false }) => {
  return (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
        enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      role="switch"
      aria-checked={enabled}
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
    >
      <span className="sr-only">Enable two-factor authentication</span>
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

const MfaSetup = () => {
  const { isMfaAvailable, loading: featureFlagLoading } = useMfaFeature();
  const [showSetup, setShowSetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [factorId, setFactorId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const addToast = useToast();

  const startMfaEnrollment = async () => {
    try {
      setEnrolling(true);
      setError('');
      setSuccess('');

      console.log('Attempting MFA enrollment...');
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Authenticator App (${new Date().toLocaleDateString()})`
      });

      console.log('MFA enrollment response:', { data, error });

      if (error) {
        console.error('MFA enrollment error:', error);
        // Show the actual error from Supabase
        setError(`MFA enrollment failed: ${error.message}`);
        return;
      }

      setFactorId(data.id);
      setSecret(data.totp.secret);

      // Generate QR code
      const user = (await supabase.auth.getUser()).data.user;
      const issuer = 'Orgadoro';
      const accountName = user?.email || 'user';
      const otpauth = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${data.totp.secret}&issuer=${encodeURIComponent(issuer)}`;
      
      const qrCodeDataUrl = await QRCode.toDataURL(otpauth);
      setQrCodeUrl(qrCodeDataUrl);
      setShowSetup(true);

    } catch (error) {
      console.error('Error starting MFA enrollment:', error);
      setError(`Failed to start MFA enrollment: ${error.message || error.toString()}`);
    } finally {
      setEnrolling(false);
    }
  };

  const verifyAndEnableMfa = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setVerifying(true);
      setError('');

      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factorId,
        code: verificationCode
      });

      if (error) {
        console.error('MFA verification error:', error);
        setError(error.message || 'Invalid verification code');
        return;
      }

      setSuccess('Two-factor authentication has been successfully enabled!');
      addToast('MFA enabled successfully!', 'success');
      setShowSetup(false);
      setVerificationCode('');
      setQrCodeUrl('');
      setSecret('');
      setFactorId(null);

    } catch (error) {
      console.error('Error verifying MFA:', error);
      setError('Failed to verify MFA code');
    } finally {
      setVerifying(false);
    }
  };

  const cancelSetup = () => {
    setShowSetup(false);
    setQrCodeUrl('');
    setSecret('');
    setVerificationCode('');
    setFactorId(null);
    setError('');
    setSuccess('');
  };

  // Simple toggle handler - just attempt enrollment when toggled on
  const handleMfaToggle = async (newState) => {
    if (newState) {
      // User wants to enable MFA - attempt enrollment
      await startMfaEnrollment();
    } else {
      // User wants to disable MFA - just cancel setup for now
      cancelSetup();
    }
  };

  // Show loading state while checking feature flags
  if (featureFlagLoading) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication (2FA)
          </h2>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show unavailable message if MFA is not available
  if (!isMfaAvailable) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication (2FA)
          </h2>
        </CardHeader>
        <CardContent>
          <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-4">
            <div className="flex">
              <Shield className="h-5 w-5 text-gray-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Two-factor authentication is currently unavailable. This feature is being prepared and will be available soon.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Two-Factor Authentication (2FA)
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Add an extra layer of security to your account with TOTP authenticator apps like Google Authenticator, Authy, or 1Password.
        </p>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                <details className="mt-2">
                  <summary className="text-xs text-red-600 dark:text-red-500 cursor-pointer">
                    Technical Details
                  </summary>
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1 font-mono">
                    This error suggests MFA enrollment might be disabled in your local Supabase configuration. 
                    Check the console logs for more details.
                  </p>
                </details>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-md bg-green-50 dark:bg-green-900/20 p-3">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main MFA Toggle Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enhance your account security with an additional authentication step
              </p>
            </div>
            <Toggle
              enabled={showSetup}
              onChange={handleMfaToggle}
              disabled={enrolling || verifying}
            />
          </div>

          {/* Show setup process when toggled on */}
          {showSetup && (
            <div className="space-y-6 pl-4 border-l-2 border-blue-200 dark:border-blue-800">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Set up Two-Factor Authentication
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Scan this QR code with your authenticator app, then enter the verification code.
                </p>
              </div>

              <div className="flex flex-col items-center space-y-4">
                {qrCodeUrl && (
                  <div className="p-4 bg-white rounded-lg border">
                    <img src={qrCodeUrl} alt="QR Code for 2FA setup" className="w-48 h-48" />
                  </div>
                )}

                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Can't scan the QR code? Enter this code manually:
                  </p>
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                    <code className="text-sm font-mono break-all">{secret}</code>
                  </div>
                </div>
              </div>

              <form onSubmit={verifyAndEnableMfa} className="space-y-4">
                <FormGroup>
                  <FormLabel htmlFor="verification-code">
                    Enter verification code from your authenticator app
                  </FormLabel>
                  <FormInput
                    id="verification-code"
                    type="text"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    required
                    className="text-center text-lg tracking-wider"
                  />
                </FormGroup>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={verifying || verificationCode.length !== 6}
                    className="flex-1"
                  >
                    {verifying ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      'Verify and Enable'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelSetup}
                    disabled={verifying}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Show loading state */}
          {enrolling && !showSetup && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Setting up MFA...</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MfaSetup;
